import { z } from 'zod';

/**
 * Schema for HeaderTab
 * Contains fields for the header section of the customer form
 */
export const headerTabSchema = z.object({
  // Basic customer information
  customer_code: z.string().min(1, { message: 'Mã khách hàng là bắt buộc' }),
  customer_name: z.string().min(1, { message: 'Tên khách hàng là bắt buộc' }),
  alternative_name: z.string().optional().nullable(),
  address: z.string().optional().nullable(),

  // Customer type and flags
  customer_type: z.string().default('doanh_nghiep'),
  is_customer: z.boolean().default(true),
  is_vendor: z.boolean().default(false),

  // Contact information
  contact_person: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  tax_code: z.string().optional().nullable(),
  website: z.string().optional().nullable()
});

/**
 * Schema for GeneralInfoTab
 * Contains fields for the general information tab
 */
export const generalInfoTabSchema = z.object({
  // Credit limit
  credit_limit: z
    .union([z.number(), z.string().transform(val => Number(val) || 0)])
    .refine(val => val >= 0, { message: 'Không được nhập số âm' })
    .optional()
    .nullable()
});

/**
 * Schema for OtherTab
 * Contains fields for the other information tab
 */
export const otherTabSchema = z.object({
  // Personal information
  birth_date: z.string().optional().nullable(),
  id_number: z.string().optional().nullable(),
  id_issue_date: z.string().optional().nullable(),
  id_issue_place: z.string().optional().nullable(),

  // Representative information
  legal_representative: z.string().optional().nullable(),
  representative_position: z.string().optional().nullable(),

  // Bank information
  bank_account: z.string().optional().nullable(),
  bank_name: z.string().optional().nullable(),
  bank_branch: z.string().optional().nullable(),

  // Location information
  province: z.string().optional().nullable(),

  // Status and description
  status: z.string().default('1'),
  description: z.string().optional().nullable()
});

/**
 * Schema for AdditionalInfoTab
 * Contains fields for the additional information tab
 */
export const additionalInfoTabSchema = z.object({
  additional_info: z.record(z.unknown()).optional().nullable()
});

/**
 * Schema for HddtTab
 * Contains fields for the electronic invoice tab
 */
export const hddtTabSchema = z.object({
  // Electronic invoice settings
  use_einvoice: z.coerce.string(),
  einvoice_email: z.string().optional().nullable(),
  einvoice_phone: z.string().optional().nullable(),
  einvoice_address: z.string().optional().nullable(),
  representative: z.string().optional().nullable()
});

/**
 * Combined schema for the entire customer form
 */
export const khachHangSchema = headerTabSchema
  .merge(generalInfoTabSchema)
  .merge(otherTabSchema)
  .merge(additionalInfoTabSchema)
  .merge(hddtTabSchema);

/**
 * Type for the form values
 */
export type KhachHangFormValues = z.infer<typeof khachHangSchema>;

/**
 * Initial values for the customer form
 */
export const initialKhachHangValues = {
  customer_code: '',
  customer_name: '',
  alternative_name: null,
  address: null,
  customer_type: 'doanh_nghiep',
  is_customer: true,
  is_vendor: false,
  contact_person: null,
  phone: null,
  email: null,
  tax_code: null,
  website: null,
  credit_limit: 0,
  notes: null,
  birth_date: null,
  id_number: null,
  id_issue_date: null,
  id_issue_place: null,
  legal_representative: null,
  representative_position: null,
  bank_account: null,
  bank_name: null,
  bank_branch: null,
  province: null,
  status: '1',
  description: null,
  additional_info: null,
  use_einvoice: '0',
  einvoice_email: null,
  einvoice_phone: null,
  einvoice_address: null,
  einvoice_contact: null
};
